/**
 * This hook is used to handle events related to niramaya.
 *
 * It receives two parameters: an observable and a setter function
 */

import { useEffect } from "react";
import { EventType } from "../../../service/network/utils/EventType";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { visitUpdated } from "../../../components/recoil/visitListRecoil";
import { deviceport } from "../../../service/memory/recoil/deviceportstore";
import { formUpdated, patientDetailsUpdated } from "../../../components/recoil/formsRecoils";

export const useObservable = (observable, setter) => {
	const setVisitDetails = useSetRecoilState(visitUpdated);
	const setFormDetails = useSetRecoilState(formUpdated);
	const setPatientDetails = useSetRecoilState(patientDetailsUpdated);
	const devicePort = useRecoilValue(deviceport);

	useEffect(() => {
		let subscription = observable.subscribe((result) => {
			//subscribes to the observer
			switch (result?.type) {
				case EventType.PATIENT_VISIT_ASSIGNED:
					setVisitDetails(result?.payload); //stores the payload received in recoil state
					setter(true);
					break;
				case EventType.PATIENT_VISIT_ACCEPTED:
					setVisitDetails(result?.payload); //stores the payload received in recoil state
					setter(true);break;
				case EventType.PATIENT_VISIT_REJECTED:
					setter(true);
					break;
				case EventType.PATIENT_VISIT_UPDATED:
					setVisitDetails(result?.payload);
					setter(true);
					if (result?.payload?.visitStatus === "Started")
						devicePort?.postMessage(
							JSON.stringify({
								functionName: "visitStarted",
								data: result?.payload,
							})
						);
					else if (result?.payload?.visitStatus === "Completed")
						devicePort?.postMessage(
							JSON.stringify({
								functionName: "visitCompleted",
								data: result?.payload,
							})
						);

					break;
				case EventType.PATIENT_VISIT_RE_ASSIGN:
					setVisitDetails(result?.payload);
					setter(true);
					break;
				case EventType.PATIENT_FORMS_SUBMITTED:
					setFormDetails(result?.payload);
					setter(true);
					break;
				case EventType.PATIENT_FORMS_UPDATED:
					setFormDetails(result?.payload);
					setter(true);
					break;
				case EventType.OPPORTUNITY_ACCEPTED:
					setter(true);
					break;
				case EventType.OPPORTUNITY_REJECTED:
					setter(true);
					break;
				case EventType.DELIVERY_ADDED:
					setter(true);
					break;
				case EventType.DELIVERY_UPDATED:
					setVisitDetails(result?.payload);
					setter(true);
					break;
				case EventType.CAREPLAN_ADDED:
					setter(true);
					break;
				case EventType.PATIENT_DETAILS_UPDATED:
					setPatientDetails(result?.payload);
					setter(true);
					break;
				case EventType.PREFERENCE_TAG_CREATED:
					setter(true);
					break;
				case EventType.PREFERENCE_TAG_UPDATE:
					setter(true);
					break;
				case EventType.PREFERENCE_TAG_DELETED:
					setter(true);
					break;
				case EventType.ENTITY_TAG_DELETED:
					setter(true);
					break;
				case EventType.ENTITY_TAG_UPDATED:
					setter(true);
					break;
				case EventType.ENTITY_TAG_CREATED:
					setter(true);
					break;
				case EventType.GROUP_ADDED:
				case EventType.GROUP_UPDATED:
				case EventType.GROUP_MEMBERSHIP_UPDATED:
				case EventType.GROUP_MEMBERSHIP_DELETED:
					setter(true);
					break;
				case EventType.API_RESPONSE_RECEIVED:
					setter(true);
					break;
				case EventType.PATIENT_TEAM_MEMBER_RESPONSE:
					setter(true);
					break;
				case EventType.PATIENT_DATA_UPDATED:
					setter(true);
					break;
				case EventType.WORKFORCE_DATA_UPDATED:
					setter(true);
					break;
				case EventType.WORKFORCE_PIN_RESET:
					setter(true);
					break;
				case EventType.REPORT_DATA_UPADTED:
					setter(true);
					break;
				case EventType.REPORT_DELETED:
					setter(true);
					break;
				case EventType.REPORT_SAVE_AS_NEW:
					setter(true);
					break;
				case EventType.VISIT_FORM_APPROVED:
					setter(true);
					break;
				case EventType.VISIT_FORM_REJECTED:
					setter(true);
					break;
				case EventType.WORKFORCE_INVITED:
					setter(true);
					break;
				case EventType.CAREPLAN_ACCEPTED:
					setter(true);
					break;
				case EventType.CAREPLAN_REJECTED:
					setter(true);
					break;
				default:
					break;
			}
		});

		return () => {
			subscription.unsubscribe(); //unsubscribes from the observer when not required
		};
	}, [observable, setter, devicePort]);
};
